import React from "react";
import { Box, Stack, Typography } from "@mui/material";
import palette from "src/theme/palette";
import { TIMEZONE_OPTIONS } from "@consts";
import HyroField from "@hyro-components/fields/HyroField";
import { FIELD_TYPES } from "@hyro-components/fields/utils";
import { v4 as uuidv4 } from "uuid";
import { ASSISTANT_SETTINGS_TAB_SECTIONS } from "src/pages/admin/GeneralSettingsConsts";

const GeneralSettingsTab = ({ assistantSettingsProps, settingsInitialData, isReadOnly }) => {
  const {
    accountName, clientName, friendlyName, errorResponseText,
    agentTimezone, accountTimezone, dashboardId, skillsDashboards, isTestAgent
  } = assistantSettingsProps;

  const timezoneOptions = TIMEZONE_OPTIONS.map((option) =>
    option.key === accountTimezone?.value ? { ...option, chipText: "Account default" } : option
  );

  const skillsDashboardsOptions = settingsInitialData?.options?.skillsDashboards;
  const options = skillsDashboardsOptions?.map(option => ({
    display_key: option,
    key: uuidv4()
  })) || [];

  return (
    <Stack direction="column" gap={2}>
      <Typography variant="h6">General settings</Typography>

      <Stack direction="column" gap={3}>
        <Box sx={{ bgcolor: palette.system[0], border: 1, borderColor: palette.grey[200], borderRadius: 1, p: 3 }}>
          <Box sx={{ maxWidth: 552 }}>
            <Stack direction="column" gap="24px">
              <HyroField
                fieldType={FIELD_TYPES.TEXT}
                label={ASSISTANT_SETTINGS_TAB_SECTIONS.ACCOUNT}
                value={accountName?.value}
                isReadOnly
                showTopSection
                isFieldReadOnlyFromSpec={true}
              />

              <HyroField
                fieldType={FIELD_TYPES.TEXT}
                label={ASSISTANT_SETTINGS_TAB_SECTIONS.CONFIGURATION_ID}
                value={clientName?.value}
                isReadOnly
                showTopSection
                isFieldReadOnlyFromSpec={true}
              />

              <HyroField
                testId="assistant-name-input"
                fieldType={FIELD_TYPES.TEXT}
                label={ASSISTANT_SETTINGS_TAB_SECTIONS.ASSISTANT_NAME}
                placeholder={ASSISTANT_SETTINGS_TAB_SECTIONS.ASSISTANT_NAME}
                value={friendlyName?.value}
                onChange={friendlyName?.handleChange}
                onBlur={() => friendlyName?.setErrorText(friendlyName?.value ? "" : "Please enter a name")}
                error={friendlyName?.errorText}
                isReadOnly={isReadOnly}
                showTopSection
              />

              <HyroField
                fieldType={FIELD_TYPES.TEXT}
                label={ASSISTANT_SETTINGS_TAB_SECTIONS.ERROR_RESPONSE_TEXT}
                placeholder="Enter error response text"
                value={errorResponseText?.value}
                onChange={errorResponseText?.handleChange}
                isReadOnly={isReadOnly}
                showTopSection
              />

              <HyroField
                fieldType={FIELD_TYPES.CHOOSE_ONE}
                onChange={agentTimezone?.handleChange}
                value={agentTimezone?.value || accountTimezone?.value}
                data-testid="assistant-timezone-select"
                testId="agent-timezone"
                name="agentTimezone"
                label="Time zone"
                description="Overrides account default for this agent."
                options={timezoneOptions}
                fullWidth
                isReadOnly={isReadOnly}
                showTopSection
              />

              <HyroField
                testId="test-agent-field"
                fieldType={FIELD_TYPES.BOOL}
                name="isTestAgent"
                label={ASSISTANT_SETTINGS_TAB_SECTIONS.TEST_AGENT.TITLE}
                description={ASSISTANT_SETTINGS_TAB_SECTIONS.TEST_AGENT.HELPER_TEXT}
                value={isTestAgent?.value}
                onChange={isTestAgent?.handleChange}
                isReadOnly={isReadOnly}
                showTopSection
              />
            </Stack>
          </Box>
        </Box>

        <Box sx={{ bgcolor: palette.system[0], border: 1, borderColor: palette.grey[200], borderRadius: 1, p: 3 }}>
          <Typography variant="subtitle1" mb={2}>{ASSISTANT_SETTINGS_TAB_SECTIONS.LOOKER.TITLE}</Typography>
          <Box sx={{ maxWidth: 552 }}>
            <Stack direction="column" gap="24px">
              <HyroField
                fieldType={FIELD_TYPES.TEXT}
                label={ASSISTANT_SETTINGS_TAB_SECTIONS.LOOKER.FIELDS.DASHBOARD_ID}
                placeholder={ASSISTANT_SETTINGS_TAB_SECTIONS.LOOKER.FIELDS.DASHBOARD_ID}
                value={dashboardId?.value}
                error={dashboardId?.errorText}
                onChange={dashboardId?.handleChange}
                isReadOnly={isReadOnly}
                showTopSection
              />

              <HyroField
                fieldType={FIELD_TYPES.CHOOSE_AT_LEAST_ONE}
                label={ASSISTANT_SETTINGS_TAB_SECTIONS.LOOKER.FIELDS.SKILLS_DASHBOARDS}
                placeholder="Select a skill"
                value={skillsDashboards?.value}
                onChange={skillsDashboards?.handleChange}
                options={options}
                error={skillsDashboards?.errorText}
                showTopSection
              />
            </Stack>
          </Box>
        </Box>
      </Stack>
    </Stack>
  );
};

export default GeneralSettingsTab;
